import KmxBadgeCheckFilled from '@kmx/mui-icons/KmxBadgeCheckFilled';
import KmxExclamationContainerCircleFilled from '@kmx/mui-icons/KmxExclamationContainerCircleFilled';
import KmxInfoCircleFilled from '@kmx/mui-icons/KmxInfoCircleFilled';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';

import { formatCurrency } from '@/helpers/formatters';
import type {
  CarTileFbsInformationWithDecision,
  FinanceTerm,
} from '@/types/FinanceHelperTypes';
import type { NonEmptyArray } from '@/types/utilityTypes';

import DialogTitleWithCloseButton from '../DialogTitleWithCloseButton/DialogTitleWithCloseButton';
import {
  FbsUnavailable,
  HighDownPaymentBody,
  HighDownPaymentDisclaimer,
  InformationUnavailableBody,
  PrequalificationDisclaimer,
} from '../Finance/TermsModalContent/FinanceTermsModalContent';
import AvailableTerms from './AvailableTerms';
import PriceTable from './PriceTable';

type FbsModalProps = {
  href: string;
  open: boolean;
  basePrice: number;
  handleClose: () => void;
  fbsInformation: CarTileFbsInformationWithDecision;
};

type FbsModalContentType = {
  icon: React.ReactNode;
  title: React.ReactNode;
  body?: React.ReactNode;
  disclaimerText?: React.ReactNode;
};

const StyledSup = styled('sup')(() => ({ fontSize: '0.6em' }));

const FbsModal = ({
  href,
  open,
  basePrice,
  handleClose,
  fbsInformation,
}: FbsModalProps) => {
  const {
    terms,
    decision,
    downPayment,
    estimatedFees,
    conditionAmount,
    tradeInEquityAmount,
    finalVehicleFinancePrice,
    fbsModalButtonClickHandler,
  } = fbsInformation;

  const modalContent: FbsModalContentType = useMemo(() => {
    switch (decision) {
      case 'preApproved':
        return {
          icon: <KmxBadgeCheckFilled />,
          title: (
            <span>
              Pre-qualified with &nbsp;
              {formatCurrency(conditionAmount! + downPayment!)} down
              <StyledSup>‡</StyledSup>
            </span>
          ),
          disclaimerText: <PrequalificationDisclaimer />,
        };

      case 'additionalDown':
        return {
          icon: <KmxExclamationContainerCircleFilled />,
          title: (
            <span>
              Pre-qualified with an additional &nbsp;
              {formatCurrency(conditionAmount!)} down
              <StyledSup>‡</StyledSup>
            </span>
          ),
          disclaimerText: <PrequalificationDisclaimer />,
        };

      case 'notNeeded':
        return {
          icon: <KmxInfoCircleFilled />,
          title: 'Purchase with your down payment',
          body: <HighDownPaymentBody />,
          disclaimerText: <HighDownPaymentDisclaimer />,
        };

      case 'declined':
        return {
          icon: <KmxInfoCircleFilled />,
          title: 'Pre-qualification unavailable',
          body: <FbsUnavailable />,
          disclaimerText: undefined,
        };

      default:
        return {
          icon: <KmxInfoCircleFilled />,
          title: 'Information unavailable',
          body: <InformationUnavailableBody />,
          disclaimerText: undefined,
        };
    }
  }, [conditionAmount, decision, downPayment]);

  const hasTerms = decision === 'preApproved' || decision === 'additionalDown';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      data-is-modal="true"
      className="kmx-car-tile__fbs-terms-modal"
    >
      <DialogTitleWithCloseButton
        variant="h5"
        sx={{ pb: 0 }}
        onClose={handleClose}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          {modalContent.icon}
          {modalContent.title}
        </Stack>
      </DialogTitleWithCloseButton>

      <DialogContent>
        {hasTerms ? (
          <>
            {terms && terms.length >= 1 && (
              <AvailableTerms terms={terms as NonEmptyArray<FinanceTerm>} />
            )}
            <PriceTable
              basePrice={basePrice}
              downPayment={downPayment!}
              estimatedFees={estimatedFees!}
              conditionAmount={conditionAmount!}
              tradeInEquityAmount={tradeInEquityAmount}
              finalVehicleFinancePrice={finalVehicleFinancePrice!}
            />

            <Button
              href={href}
              color="primary"
              sx={{ my: 2 }}
              variant="contained"
              data-ignore-anchor-tag-rule
              onClick={fbsModalButtonClickHandler}
            >
              View All Car Details
            </Button>
          </>
        ) : (
          modalContent.body
        )}
        {modalContent.disclaimerText}
      </DialogContent>
    </Dialog>
  );
};

export default FbsModal;
